import type { IconDefinition } from "@/icons/solid-icons";
import type { QRScannerStatus } from "@/types/login/QRScanner";
import type { ButtonType } from "@dinesapp/dines-ui/components/Buttons/Types/Button";
import type { IFormLayoutConfig } from "@dinesapp/dines-ui/components/Form/Types/Form";
import type { FormApi, Validator } from "@tanstack/react-form";
import type {
  ClipboardEvent,
  FocusEvent,
  KeyboardEvent,
  RefObject,
} from "react";

/**
 * Interface representing the values held by the login form.
 * @interface ILoginFormValues
 * @property {string} username: The account email address.
 * @property {string} password: The account password.
 */
export interface ILoginFormValues {
  username: string;
  password: string;
}

/**
 * Type for the TanStack form instance shared with dines-ui CustomForm.
 */
export type LoginFormApi = FormApi<
  ILoginFormValues,
  Validator<ILoginFormValues, unknown>
>;

/**
 * Enum for the current visible section of the login screen.
 * @enum LoginSection
 */
export enum LoginSection {
  ForgotPassword = "Forgot Password",
  Login = "Login",
  OTP = "OTP",
}

/**
 * Interface representing the view model for the forgot-password success alert.
 * @interface IForgotPasswordAlertViewModel
 * @property {Object} labels: Copy for the alert.
 * @property {string} labels.alertTitle: Success message body.
 * @property {IconDefinition} icon: FontAwesome icon shown in the alert.
 * @property {boolean} dataActive: Data attribute reflecting the alert's active state.
 */
export interface IForgotPasswordAlertViewModel {
  labels: {
    alertTitle: string;
  };
  icon: IconDefinition;
  dataActive: boolean;
}

/**
 * Interface representing the alert state for the forgot-password flow.
 * @interface IForgotPasswordAlertState
 * @property {boolean} success: Whether the request succeeded.
 * @property {boolean} showAlert: Whether the alert is currently rendered.
 */
export interface IForgotPasswordAlertState {
  success: boolean;
  showAlert: boolean;
}

/**
 * Interface representing an inline auth prompt row with leading text and a single linked phrase, used for the "Or, use a One Time Passcode" link that switches to the OTP section.
 * @interface IAuthPromptViewModel
 * @property {boolean} isVisible: Whether the prompt row should be rendered.
 * @property {string} leadingText: Leading copy shown before the link (e.g. "Or, use a").
 * @property {string} linkText: Anchor text for the linked phrase (e.g. "One Time Passcode").
 * @property {Function} onClick: Handler that switches the form to the OTP section.
 */
export interface IAuthPromptViewModel {
  isVisible: boolean;
  leadingText: string;
  linkText: string;
  onClick: () => void;
}

/**
 * Interface representing the venue-login redirect chip shown beneath the login form, linking venue users to the Partner Dashboard.
 * @interface IPartnerLoginViewModel
 * @property {boolean} isVisible: Whether the redirect chip should be rendered.
 * @property {string} href: Destination URL of the Partner Dashboard login.
 * @property {Object} labels: Copy used in the chip.
 * @property {string} labels.title: Primary copy on the chip (e.g. "Running a single venue?").
 * @property {string} labels.subtitle: Supporting copy on the chip (e.g. "Go to the venue login").
 * @property {string} labels.ariaLabel: Accessible label combining title and subtitle for screen readers.
 */
export interface IPartnerLoginViewModel {
  isVisible: boolean;
  href: string;
  labels: {
    title: string;
    subtitle: string;
    ariaLabel: string;
  };
}

/**
 * Interface representing the inline "Forgot password?" trigger shown beneath the password field.
 * @interface IForgotPasswordTriggerViewModel
 * @property {boolean} isVisible: Whether the trigger should be rendered.
 * @property {string} text: Anchor text (e.g. "Forgot password?").
 * @property {Function} onClick: Handler that switches the form to the forgot-password section.
 */
export interface IForgotPasswordTriggerViewModel {
  isVisible: boolean;
  text: string;
  onClick: () => void;
}

/**
 * Interface representing the page footer row view model.
 * @interface IFooterViewModel
 * @property {Object} labels: Copy and links for the footer.
 * @property {string} labels.copyrightText: Copyright notice (e.g. "© Dines 2026").
 * @property {string} labels.privacyText: Anchor text for the privacy link.
 * @property {string} labels.privacyHref: URL for the privacy policy.
 * @property {string} labels.termsText: Anchor text for the terms link.
 * @property {string} labels.termsHref: URL for the terms of service.
 * @property {string} labels.needHelpText: Heading shown above the help link.
 * @property {string} labels.chatToUsText: Anchor text for the live-chat link.
 * @property {string} labels.chatLink: URL for the live-chat link.
 */
export interface IFooterViewModel {
  labels: {
    copyrightText: string;
    privacyText: string;
    privacyHref: string;
    termsText: string;
    termsHref: string;
    needHelpText: string;
    chatToUsText: string;
    chatLink: string;
  };
}

/**
 * Interface representing an action button rendered by the shared login form.
 * @interface ILoginFormAction
 * @property {string} id: Unique identifier for the action.
 * @property {string} text: Text rendered on the action button.
 * @property {string} loadingText: Text shown on the action while a submission is in flight.
 * @property {ButtonType} buttonType: Variant of button for the action.
 * @property {boolean} isVisible: Whether the action is currently visible.
 * @property {boolean} isSubmitButton: Whether the action submits the form on click.
 * @property {boolean} isSubmitting: Whether the action is currently submitting and should disable.
 * @property {Function} onClick: Click handler for the action.
 */
export interface ILoginFormAction {
  id: string;
  text: string;
  loadingText: string;
  buttonType: ButtonType;
  isVisible: boolean;
  isSubmitButton: boolean;
  isSubmitting: boolean;
  onClick: () => void;
}

/**
 * Interface representing the dines-ui form view model shared by every login section.
 * @interface ILoginFormViewModel
 * @property {Object} labels: Labels for the form wrapper.
 * @property {string} labels.id: Unique identifier passed to the underlying dines-ui form.
 * @property {LoginFormApi} form: The dines-ui form instance produced by `useForm`.
 * @property {IFormLayoutConfig} layout: Section-aware dines-ui layout config.
 * @property {Array<ILoginFormAction>} actions: Section-aware action buttons rendered beneath the form.
 * @property {string} errorMessage: Inline error message to show above the form (empty string when no error).
 * @property {IForgotPasswordTriggerViewModel} forgotPasswordTrigger: Inline "Forgot password?" trigger rendered between the form fields and the primary action.
 * @property {ILoginFormAction} [scanAction]: Optional full-width secondary action that opens the QR scanner (OTP section only).
 * @property {ICodeSegmentsProps} [codeSegments]: Optional segmented code input rendered instead of the dines-ui form (OTP section only).
 */
export interface ILoginFormViewModel {
  labels: {
    id: string;
  };
  form: LoginFormApi;
  layout: IFormLayoutConfig;
  actions: Array<ILoginFormAction>;
  errorMessage: string;
  forgotPasswordTrigger: IForgotPasswordTriggerViewModel;
  scanAction?: ILoginFormAction;
  codeSegments?: ICodeSegmentsProps;
}

/**
 * Interface representing the top-level login view model.
 * @interface ILoginViewModel
 * @property {IForgotPasswordAlertState} forgotPasswordAlert: Forgot-password alert state.
 * @property {Object} labels: Top-level copy and handlers.
 * @property {string} labels.eyebrow: Uppercase tagline above the heading (e.g. "Welcome to Dines").
 * @property {string} labels.headingLineOne: First line of the main page heading (e.g. "Welcome").
 * @property {string} labels.headingLineTwo: Second line of the main page heading (e.g. "back.").
 * @property {string} labels.subtitle: Supporting copy shown below the heading.
 * @property {string} labels.logoHref: External URL the logo links to (the Dines website).
 * @property {string} labels.logoAlt: Alt text for the logo image.
 * @property {string} labels.logoAriaLabel: Screen-reader label describing the logo link's action.
 * @property {boolean} isAutomaticallyLoggingIn: Whether an automatic OTP sign-in is in flight.
 * @property {ILoginFormViewModel} formVm: Shared form view model.
 * @property {IForgotPasswordAlertViewModel} forgotPasswordAlertViewModel: View model for the forgot-password success alert.
 * @property {IAuthPromptViewModel} otpEntry: View model for the "use a one-time passcode" link that opens the OTP section.
 * @property {IPartnerLoginViewModel} partnerLogin: View model for the venue-login redirect chip beneath the form.
 * @property {IFooterViewModel} footerViewModel: View model for the page footer row.
 * @property {boolean} isScanning: Whether the QR scanner overlay is currently open.
 * @property {IQRScannerViewModel} qrScanner: Composed view model for the QR scanner overlay.
 */
export interface ILoginViewModel {
  forgotPasswordAlert: IForgotPasswordAlertState;
  labels: {
    eyebrow: string;
    headingLineOne: string;
    headingLineTwo: string;
    subtitle: string;
    logoHref: string;
    logoAlt: string;
    logoAriaLabel: string;
  };
  isAutomaticallyLoggingIn: boolean;
  formVm: ILoginFormViewModel;
  forgotPasswordAlertViewModel: IForgotPasswordAlertViewModel;
  otpEntry: IAuthPromptViewModel;
  partnerLogin: IPartnerLoginViewModel;
  footerViewModel: IFooterViewModel;
  isScanning: boolean;
  qrScanner: IQRScannerViewModel;
}

/**
 * Interface representing the view model for the OTP QR-code scanner overlay.
 * @interface IQRScannerViewModel
 * @property {RefObject<HTMLVideoElement | null>} videoRef: Ref attached to the live camera video element.
 * @property {RefObject<HTMLCanvasElement | null>} canvasRef: Ref attached to the hidden decode canvas element.
 * @property {QRScannerStatus} status: Current camera lifecycle status.
 * @property {string} errorMessage: User-facing message shown when status is Error (empty otherwise).
 * @property {RefObject<HTMLButtonElement | null>} closeRef: Ref for the close button, focused on open.
 * @property {Function} onCancel: Handler that stops the camera and closes the overlay.
 * @property {Object} labels: Copy shown in the overlay.
 * @property {string} labels.title: Accessible dialog title (e.g. "Scan QR code").
 * @property {string} labels.prompt: Instructional prompt shown while scanning (e.g. "Point at the QR code").
 * @property {string} labels.closeAriaLabel: Accessible label for the close button.
 * @property {string} labels.requesting: Message shown while awaiting camera permission.
 */
export interface IQRScannerViewModel {
  videoRef: RefObject<HTMLVideoElement | null>;
  canvasRef: RefObject<HTMLCanvasElement | null>;
  status: QRScannerStatus;
  errorMessage: string;
  closeRef: RefObject<HTMLButtonElement | null>;
  onCancel: () => void;
  labels: {
    title: string;
    prompt: string;
    closeAriaLabel: string;
    requesting: string;
  };
}

/**
 * Interface representing the parameters for the QR scanner view model.
 * @interface IQRScannerParams
 * @property {boolean} active: Whether the scanner overlay is open (drives the camera).
 * @property {Function} onDetected: Called once with the decoded code on a successful scan.
 * @property {Function} onCancel: Called when the user dismisses the scanner without scanning.
 */
export interface IQRScannerParams {
  active: boolean;
  onDetected: (code: string) => void;
  onCancel: () => void;
}

/**
 * Interface representing the props for the dumb QR scanner overlay component.
 * @interface IQRScannerProps
 * @property {IQRScannerViewModel} vm: View model produced by QRScannerViewModel.
 */
export interface IQRScannerProps {
  vm: IQRScannerViewModel;
}

/**
 * Interface representing the props for the segmented verification-code input.
 * @interface ICodeSegmentsProps
 * @property {string} value: Current bare (hyphen-free) code characters.
 * @property {Function} onChange: Called with the updated bare code string on every edit.
 * @property {number} length: Total number of character cells.
 * @property {number} groupSize: Number of cells between hyphen separators.
 * @property {string} ariaLabel: Accessible label for the group of cells.
 * @property {Function} onSubmit: Called when the user presses Enter in a cell.
 * @property {boolean} [autoFocus]: Whether to focus the first empty cell on mount.
 */
export interface ICodeSegmentsProps {
  value: string;
  onChange: (chars: string) => void;
  length: number;
  groupSize: number;
  ariaLabel: string;
  onSubmit: () => void;
  autoFocus?: boolean;
}

/**
 * Interface representing the view model for the segmented code input.
 * @interface ICodeSegmentsViewModel
 * @property {RefObject<Array<HTMLInputElement | null>>} cellRefs: Refs for each character cell.
 * @property {Array<string>} cells: Current character in each cell ('' when empty).
 * @property {Function} onCellChange: Handles typed or pasted input for a cell (index, value).
 * @property {Function} onCellKeyDown: Handles Backspace and arrow navigation for a cell.
 * @property {Function} onCellPaste: Handles pasting a code starting at a cell.
 * @property {Function} onCellFocus: Selects the cell contents on focus.
 * @property {Function} hasSeparatorAfter: Whether a hyphen separator follows the given cell index.
 */
export interface ICodeSegmentsViewModel {
  cellRefs: RefObject<Array<HTMLInputElement | null>>;
  cells: Array<string>;
  onCellChange: (index: number, value: string) => void;
  onCellKeyDown: (
    index: number,
    event: KeyboardEvent<HTMLInputElement>,
  ) => void;
  onCellPaste: (index: number, event: ClipboardEvent<HTMLInputElement>) => void;
  onCellFocus: (event: FocusEvent<HTMLInputElement>) => void;
  hasSeparatorAfter: (index: number) => boolean;
}
